/**
 * PostHog Support chat, the client-safe half: the ticket and message shapes
 * the admin inbox renders, the mapping from PostHog's API rows to them, and
 * the TipTap → markdown serializer the composer uses.
 *
 * PostHog stores every message twice: `content` is markdown (what the widget,
 * email and Slack channels deliver) and `rich_content` is the TipTap document
 * the PostHog UI edits. A reply posted from here sends both, so it renders the
 * same in PostHog's own inbox as one typed there. The serializer is a port of
 * PostHog's `serializeToMarkdown` (products/conversations/frontend/components/
 * Editor/SupportEditor.tsx) minus the @mention node, which this editor lacks.
 *
 * No server imports here: the route components import this module directly.
 * The HTTP client lives in posthog-chat.server.ts.
 */
import type { JSONContent } from '@tiptap/core'

export const TICKET_STATUSES = [
  'new',
  'open',
  'pending',
  'on_hold',
  'resolved',
] as const
export type TicketStatus = (typeof TICKET_STATUSES)[number]

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  new: 'New',
  open: 'Open',
  pending: 'Pending',
  on_hold: 'On hold',
  resolved: 'Resolved',
}

/** Everything a support person still has to deal with: the inbox default. */
export const UNRESOLVED_STATUSES: readonly TicketStatus[] = [
  'new',
  'open',
  'pending',
  'on_hold',
]

/** Inbox filter: a status, everything unresolved, or everything. */
export const CHAT_LIST_FILTERS = [
  'unresolved',
  ...TICKET_STATUSES,
  'all',
] as const
export type ChatListFilter = (typeof CHAT_LIST_FILTERS)[number]

export const CHAT_LIST_FILTER_LABELS: Record<ChatListFilter, string> = {
  unresolved: 'Unresolved',
  ...TICKET_STATUS_LABELS,
  all: 'All',
}

export const CHANNEL_LABELS: Record<string, string> = {
  widget: 'Widget',
  email: 'Email',
  slack: 'Slack',
  teams: 'Teams',
  github: 'GitHub',
}

export const TICKET_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const
export type TicketPriority = (typeof TICKET_PRIORITIES)[number]

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

/**
 * What a message is and whether the customer saw it. Only `customer_message`
 * and `sent_reply` ever reach the customer; the other two are team-only.
 */
export type ChatMessageType =
  | 'customer_message'
  | 'sent_reply'
  | 'internal_note'
  | 'ai_draft'

// ---------------------------------------------------------------------------
// PostHog API rows (the subset read here)
// ---------------------------------------------------------------------------

export interface RawTicketPerson {
  id: string
  name: string
  distinct_ids: string[]
  properties: Record<string, unknown>
  is_identified: boolean
}

export interface RawTicket {
  id: string
  ticket_number: number
  channel_source: string
  channel_detail: string | null
  distinct_id: string | null
  status: string
  priority: string | null
  anonymous_traits: Record<string, unknown> | null
  identity_verified: boolean | null
  created_at: string
  updated_at: string
  message_count: number
  last_message_at: string | null
  last_message_text: string | null
  unread_team_count: number
  session_context: Record<string, unknown> | null
  tags: string[]
  person: RawTicketPerson | null
  email_subject: string | null
  email_from: string | null
}

export interface RawMessage {
  id: string
  message_type: ChatMessageType
  content: string
  rich_content: JSONContent | null
  author_type: string
  author_name: string
  author_email: string | null
  is_private: boolean
  version: number
  created_at: string
}

// ---------------------------------------------------------------------------
// What the inbox renders
// ---------------------------------------------------------------------------

export interface ChatPerson {
  id: string
  name: string
  distinctIds: string[]
  isIdentified: boolean
  /** The person's page in PostHog. */
  posthogUrl: string
}

export interface ChatTicket {
  id: string
  number: number
  status: TicketStatus
  priority: TicketPriority | null
  /** widget, email, slack, teams, github */
  channel: string
  channelDetail: string | null
  distinctId: string | null
  /**
   * Whether PostHog's server attested the identity (widget HMAC, SPF email).
   * `null` predates the signal. An unverified email or name is whatever the
   * browser sent, so never act on account matters from it alone.
   */
  identityVerified: boolean | null
  customerName: string | null
  customerEmail: string | null
  createdAt: string
  updatedAt: string
  lastMessageAt: string | null
  lastMessageText: string | null
  messageCount: number
  unreadTeamCount: number
  tags: string[]
  /** Page the widget was opened on. */
  currentUrl: string | null
  /** Session replay of the conversation, when replay was on. */
  replayUrl: string | null
  emailSubject: string | null
  person: ChatPerson | null
  /** The ticket in PostHog's own inbox, for the events/replay/exception panels. */
  posthogUrl: string
}

export interface ChatMessage {
  id: string
  type: ChatMessageType
  /** Markdown. */
  content: string
  richContent: JSONContent | null
  authorType: string
  authorName: string
  authorEmail: string | null
  isPrivate: boolean
  /** Edit count; 0 means never edited. */
  version: number
  createdAt: string
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function asStatus(value: string): TicketStatus {
  return (TICKET_STATUSES as readonly string[]).includes(value)
    ? (value as TicketStatus)
    : 'open'
}

function asPriority(value: string | null): TicketPriority | null {
  return value && (TICKET_PRIORITIES as readonly string[]).includes(value)
    ? (value as TicketPriority)
    : null
}

/** `https://us.posthog.com/project/517510` */
export function posthogProjectUrl(host: string, projectId: string): string {
  return `${host.replace(/\/+$/, '')}/project/${encodeURIComponent(projectId)}`
}

/**
 * Name and email come from, in order: the traits the widget's identification
 * form collected, the person's analytics properties, then (email only) the
 * sender of an email ticket. A person with no name property is named after
 * its distinct id by PostHog, which is noise, so that case reads as no name.
 */
export function mapTicket(raw: RawTicket, projectUrl: string): ChatTicket {
  const traits = raw.anonymous_traits ?? {}
  const props = raw.person?.properties ?? {}
  const context = raw.session_context ?? {}
  const email =
    nonEmpty(traits.email) ?? nonEmpty(props.email) ?? nonEmpty(raw.email_from)
  const personName =
    raw.person && raw.person.name !== raw.distinct_id
      ? nonEmpty(raw.person.name)
      : null
  const name =
    nonEmpty(traits.name) ??
    nonEmpty(props.name) ??
    (personName !== email ? personName : null)
  const personDistinctId = raw.distinct_id ?? raw.person?.distinct_ids[0] ?? ''
  return {
    id: raw.id,
    number: raw.ticket_number,
    status: asStatus(raw.status),
    priority: asPriority(raw.priority),
    channel: raw.channel_source,
    channelDetail: raw.channel_detail,
    distinctId: raw.distinct_id,
    identityVerified: raw.identity_verified,
    customerName: name,
    customerEmail: email,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    lastMessageAt: raw.last_message_at,
    lastMessageText: raw.last_message_text,
    messageCount: raw.message_count,
    unreadTeamCount: raw.unread_team_count,
    tags: raw.tags ?? [],
    currentUrl: nonEmpty(context.current_url),
    replayUrl: nonEmpty(context.replay_url),
    emailSubject: nonEmpty(raw.email_subject),
    person: raw.person
      ? {
          id: raw.person.id,
          name: raw.person.name,
          distinctIds: raw.person.distinct_ids ?? [],
          isIdentified: raw.person.is_identified === true,
          posthogUrl: `${projectUrl}/person/${encodeURIComponent(personDistinctId)}`,
        }
      : null,
    posthogUrl: `${projectUrl}/support/tickets/${raw.ticket_number}`,
  }
}

export function mapMessage(raw: RawMessage): ChatMessage {
  return {
    id: raw.id,
    type: raw.message_type,
    content: raw.content ?? '',
    richContent: raw.rich_content ?? null,
    authorType: raw.author_type,
    authorName: raw.author_name,
    authorEmail: raw.author_email,
    isPrivate: raw.is_private,
    version: raw.version ?? 0,
    createdAt: raw.created_at,
  }
}

export function customerLabel(ticket: ChatTicket): string {
  return ticket.customerName ?? ticket.customerEmail ?? 'Anonymous visitor'
}

/**
 * Every distinct id the ticket could be filed under. A Masklabs user id is one
 * of these once the browser called `identify()` (or `setIdentity()`), so the
 * server matches them against the user table to find the account.
 */
export function candidateDistinctIds(raw: RawTicket): string[] {
  const ids = [raw.distinct_id, ...(raw.person?.distinct_ids ?? [])]
  return [...new Set(ids.filter((id): id is string => !!id))]
}

// ---------------------------------------------------------------------------
// TipTap → markdown
// ---------------------------------------------------------------------------

/** Characters that would otherwise start markdown syntax: \ ` * _ { } [ ] ( ) # + - . ! | */
function escapeMarkdown(text: string): string {
  return text.replace(/([\\`*_{}[\]()#+\-.!|])/g, '\\$1')
}

/** Only `]` and `\` can break image alt text. */
function escapeAltText(text: string): string {
  return text.replace(/([\\\]])/g, '\\$1')
}

/**
 * Bold, italic, inline code, links, images, hard breaks, bullet and ordered
 * lists (nested), code blocks. Underline has no markdown form and is dropped
 * from the text; it survives in `rich_content`.
 */
export function serializeToMarkdown(content: JSONContent): string {
  return serializeNode(content).trim()
}

function serializeNode(node: JSONContent | undefined): string {
  if (!node) return ''

  if (node.type === 'text') {
    let text = node.text ?? ''
    const marks = node.marks ?? []
    // Code is literal, so only prose gets escaped.
    if (!marks.some((m) => m.type === 'code')) text = escapeMarkdown(text)
    for (const mark of marks) {
      switch (mark.type) {
        case 'bold':
          text = `**${text}**`
          break
        case 'italic':
          text = `*${text}*`
          break
        case 'code':
          text = text.includes('`') ? `\`\` ${text} \`\`` : `\`${text}\``
          break
      }
    }
    // The link wraps whatever the other marks produced.
    const link = marks.find((m) => m.type === 'link')
    const href = link?.attrs?.href
    if (typeof href === 'string' && href) text = `[${text}](${href})`
    return text
  }

  switch (node.type) {
    case 'doc':
      return (node.content ?? []).map(serializeNode).join('')
    case 'paragraph':
      return `${(node.content ?? []).map(serializeNode).join('')}\n\n`
    case 'bulletList':
      return `${serializeListNode(node, false, '')}\n\n`
    case 'orderedList':
      return `${serializeListNode(node, true, '')}\n\n`
    case 'hardBreak':
      return '  \n'
    case 'image': {
      const src = node.attrs?.src
      // An image with no URL would be broken markdown, so it is dropped.
      if (typeof src !== 'string' || !src) return ''
      const alt = escapeAltText(
        typeof node.attrs?.alt === 'string' && node.attrs.alt
          ? node.attrs.alt
          : 'image',
      )
      return `![${alt}](${src})`
    }
    case 'codeBlock': {
      const language =
        typeof node.attrs?.language === 'string' ? node.attrs.language : ''
      const code = (node.content ?? []).map((n) => n.text ?? '').join('')
      return `\`\`\`${language}\n${code}\n\`\`\`\n\n`
    }
    case 'blockquote': {
      const inner = (node.content ?? []).map(serializeNode).join('').trim()
      return `${inner
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n')}\n\n`
    }
    default:
      return node.content ? node.content.map(serializeNode).join('') : ''
  }
}

function serializeListNode(
  node: JSONContent,
  ordered: boolean,
  indent: string,
): string {
  const rawStart = Number(node.attrs?.start ?? 1)
  const start =
    ordered && Number.isFinite(rawStart) ? Math.max(Math.trunc(rawStart), 1) : 1
  const lines: string[] = []
  let position = 0
  for (const item of node.content ?? []) {
    if (item.type !== 'listItem') continue
    const marker = ordered ? `${start + position}. ` : '- '
    position += 1
    const childIndent = indent + ' '.repeat(marker.length)
    const blocks: { text: string; nestedList: boolean }[] = []
    for (const child of item.content ?? []) {
      if (child.type === 'bulletList' || child.type === 'orderedList') {
        const nested = serializeListNode(
          child,
          child.type === 'orderedList',
          childIndent,
        )
        if (nested) blocks.push({ text: nested, nestedList: true })
      } else {
        const serialized = serializeNode(child).trim()
        if (serialized) blocks.push({ text: serialized, nestedList: false })
      }
    }
    if (blocks.length === 0) {
      lines.push((indent + marker).trimEnd())
      continue
    }
    const [first, ...rest] = blocks
    if (first.nestedList) {
      lines.push((indent + marker).trimEnd(), first.text)
    } else {
      lines.push(
        indent + marker + first.text.split('\n').join(`\n${childIndent}`),
      )
    }
    for (const block of rest) {
      lines.push(
        block.nestedList
          ? block.text
          : block.text
              .split('\n')
              .map((line) => childIndent + line)
              .join('\n'),
      )
    }
  }
  return lines.join('\n')
}
